// ─────────────────────────────────────────────────────────────────────────────
// COGNITIVE ABILITY BATTERY (CAB) — Question Bank
// 6 tests: Numerical · Verbal · Abstract · Attention · Mechanical · Diagrammatic
// Science: Cattell-Horn-Carroll (CHC) model · 3 difficulty levels per test
// ─────────────────────────────────────────────────────────────────────────────

export const CAB_TESTS = {
  numerical:     { id:"numerical",     label:"Numerical Reasoning",     icon:"🔢", time:18, items:25, desc:"Interpret data, tables and numerical information to draw accurate conclusions." },
  verbal:        { id:"verbal",        label:"Verbal Reasoning",        icon:"📝", time:20, items:24, desc:"Assess whether statements are True, False or Cannot Say based on a passage." },
  abstract:      { id:"abstract",      label:"Abstract Reasoning",      icon:"🔷", time:18, items:24, desc:"Identify the pattern rule and select the next figure in the sequence." },
  attention:     { id:"attention",     label:"Attention to Detail",     icon:"🔍", time:10, items:40, desc:"Spot errors, match information and identify discrepancies quickly and accurately." },
  mechanical:    { id:"mechanical",    label:"Mechanical Reasoning",    icon:"⚙️",  time:20, items:20, desc:"Apply understanding of mechanical and physical principles to practical situations." },
  diagrammatic:  { id:"diagrammatic",  label:"Diagrammatic Reasoning",  icon:"🔄", time:25, items:20, desc:"Follow process flow diagrams and determine correct inputs, outputs or next steps." },
};

export const CAB_NORMS = {
  numerical:    { mean:13.2, sd:3.8, n:1200 },
  verbal:       { mean:14.1, sd:4.2, n:1200 },
  abstract:     { mean:14.8, sd:3.6, n:1200 },
  attention:    { mean:26.4, sd:6.1, n:1200 },
  mechanical:   { mean:11.3, sd:3.4, n:1200 },
  diagrammatic: { mean:11.8, sd:3.2, n:1200 },
};

// Percentile bands for scoring
export const CAB_BANDS = [
  { min:0,   max:20,  label:"Developing",  color:"#dc2626", bg:"#fef2f2", hire:"Not recommended for roles with high demand on this ability." },
  { min:20,  max:40,  label:"Average",     color:"#d97706", bg:"#fffbeb", hire:"May manage adequately in roles with moderate demands." },
  { min:40,  max:70,  label:"Strong",      color:"#1d4ed8", bg:"#eff6ff", hire:"Likely to perform well. Recommended for most roles." },
  { min:70,  max:100, label:"Exceptional", color:"#059669", bg:"#f0fdf4", hire:"Top quartile. Strongly recommended for demanding roles." },
];

export function scoreCAB(testId, answers, items) {
  const correct = items.filter((item,i) => answers[i] === item.ans).length;
  const total = items.length;
  const rawPct = Math.round((correct / total) * 100);
  // z-score vs population norms
  const norm = CAB_NORMS[testId];
  const zScore = norm ? (correct - norm.mean) / norm.sd : 0;
  // Convert z to percentile (rough normal approximation)
  const percentile = Math.min(99, Math.max(1, Math.round(50 + zScore * 17)));
  const band = CAB_BANDS.find(b => percentile >= b.min && percentile < b.max) || CAB_BANDS[0];
  return { correct, total, rawPct, zScore: +zScore.toFixed(2), percentile, band };
}

export function scoreCognitiveIndex(testResults) {
  // Weighted composite: Numerical(25%) + Verbal(25%) + Abstract(25%) + Attention(15%) + Mech(5%) + Diag(5%)
  const weights = { numerical:0.25, verbal:0.25, abstract:0.25, attention:0.15, mechanical:0.05, diagrammatic:0.05 };
  let total = 0, wSum = 0;
  Object.entries(testResults).forEach(([testId, result]) => {
    if (result && weights[testId]) {
      total += result.percentile * weights[testId];
      wSum += weights[testId];
    }
  });
  const cogIndex = wSum > 0 ? Math.round(total / wSum) : 0;
  const band = CAB_BANDS.find(b => cogIndex >= b.min && cogIndex < b.max) || CAB_BANDS[0];
  return { cogIndex, band };
}

